// Factories that construct borogove runtime handles.
//
// This file is the ONLY place in src/lib/xmpp/ that imports the borogove
// runtime at value level (vs. type-only). It is split out from adapter.ts
// because `import * as borogove from "borogove"` fails to evaluate under
// tsx in Node (borogove is a Haxe-compiled browser library and references
// some symbols that don't load in a Node ESM context). Keeping value-level
// borogove out of adapter.ts means adapter.test.ts can run under tsx without
// triggering that load.
//
// Consumers should still import these from `@/lib/xmpp` (the barrel), not
// directly from this file — the barrel is the stable public surface.
//
// Tracks REQ-CN-001.B.4 in traceability-matrix.md.

import * as borogove from "borogove";
import { adaptMessage } from "./adapter.js";
import type {
	IDBPersistence,
	KeyValueStore,
	MessageMatch,
	Persistence,
	PushNotification,
	RawClient,
	RegisterClient,
	RegisterForm,
} from "./types.js";

/**
 * Construct a raw borogove Client for the given JID + persistence layer.
 * Returns the opaque `RawClient` handle. When REQ-CN-001.B.5+ migrates
 * downstream consumers to the adapted `Client` surface, the call site
 * can wrap this result with `adaptClient(client, jid)` from `./adapter.ts`.
 */
export function createClient(jid: string, persistence: Persistence): RawClient {
	return new borogove.Client(jid, persistence);
}

/**
 * Begin a registration session against the given server. Returns a promise
 * resolving to `[RegisterClient, RegisterForm]`, matching borogove's tuple
 * shape (see `Register.tsx`'s use of `useAsyncValue`).
 */
export function startRegistration(server: string): Promise<[RegisterClient, RegisterForm]> {
	return borogove.Register.start(server);
}

/**
 * Bootstrap the persistence layer for the given storage namespace.
 *
 * Always creates an IDB-backed persistence. If `preferSqlite` is true AND
 * the runtime supports the Origin-Private File System AND borogove's SQLite
 * persistence implementation is available (Safari/iOS path), an additional
 * SQLite-backed persistence is constructed and used; the IDB layer is wired
 * in as the SQLite media-store's key/value backend so attachments stay in IDB.
 *
 * The returned object exposes:
 *  - `persistence` — the composite (IDB or SQLite) handed to `Client` constructors
 *  - `idbp`        — the raw IDB handle used elsewhere (e.g. VAPID key storage)
 *  - `mediaStore`  — the underlying media-store cache; the service worker
 *                    uses this directly for `ni:// hash-path` fetch handling
 *
 * `stemmer` may be `null` to skip search indexing (the service worker does
 * this — it never runs message search).
 *
 * Tracks REQ-CN-001.B.5a/B.5be.
 */
export async function createPersistenceLayer(
	name: string,
	stemmer: ((s: string) => string) | null,
	preferSqlite: boolean,
): Promise<{
	persistence: Persistence & KeyValueStore;
	idbp: IDBPersistence;
	mediaStore: unknown;
}> {
	const mediaStore = await borogove.persistence.MediaStoreCache(name);
	const idbp = await borogove.persistence.IDB(name, mediaStore, null, stemmer);

	// borogove's SQLite persistence is not a named export in the .d.ts; it's
	// attached at runtime when the implementation is bundled. Pull it via
	// indexed access and feature-detect both the runtime API and the impl.
	const sqliteCtor = (borogove.persistence as unknown as {
		Sqlite?: new (filename: string, mediaStore: unknown) => Persistence & KeyValueStore;
	}).Sqlite;
	const opfsAvailable = typeof navigator.storage?.getDirectory === "function";

	if (preferSqlite && opfsAvailable && sqliteCtor !== undefined) {
		const persistence = new sqliteCtor(`${name}.sqlite3`, mediaStore);
		// Wire IDB into the SQLite media store as the key/value backend so
		// non-SQL data (attachments, etc.) keeps using IDB.
		(mediaStore as unknown as { setKV?: (kv: unknown) => void }).setKV?.(idbp);
		return { persistence, idbp, mediaStore };
	}

	// IDB is itself a Persistence & KeyValueStore at runtime even though the
	// declared return type is narrower.
	return {
		persistence: idbp as unknown as Persistence & KeyValueStore,
		idbp,
		mediaStore,
	};
}

/**
 * Fetch a media response from a service-worker-side media store cache.
 * Wraps the runtime-only `MediaStoreCache.getMediaResponse(uri)` method that
 * the SW polls for ni:// hash-path requests.
 *
 * Returns `null` if the store hasn't cached the URI yet — the SW retries
 * with backoff in that case.
 */
export async function getMediaResponse(
	mediaStore: unknown,
	uri: string,
): Promise<Response | null> {
	const fn = (mediaStore as { getMediaResponse?: (uri: string) => Promise<Response | null> }).getMediaResponse;
	if (typeof fn !== "function") return null;
	const r = await fn.call(mediaStore, uri);
	return r ?? null;
}

// ---------------------------------------------------------------------------
// decodePushPayload — service-worker push decryption
// ---------------------------------------------------------------------------

interface BorogovePushLike {
	readonly accountId?: unknown;
	readonly chatId?: unknown;
	readonly type?: unknown;
	readonly title?: unknown;
	readonly body?: unknown;
	readonly lang?: unknown;
	readonly imageUri?: unknown;
	readonly timestamp?: unknown;
	readonly callStatus?: unknown;
}

function pushType(raw: unknown): "call" | "chat" | "other" {
	if (raw === borogove.MessageType.MessageCall) return "call";
	if (raw === borogove.MessageType.MessageChat) return "chat";
	return "other";
}

function nonEmptyOrNull(v: unknown): string | null {
	return typeof v === "string" && v.length > 0 ? v : null;
}

/**
 * Decode an encrypted push-payload string into a typed PushNotification.
 *
 * Wraps `borogove.Push.receive(raw, persistence)` and normalizes the result
 * so the service worker doesn't need to import borogove. The `type` field is
 * mapped from borogove's `MessageType` enum to a string union.
 *
 * Tracks REQ-CN-001.B.5be.
 */
export function decodePushPayload(
	raw: string,
	persistence: Persistence,
): PushNotification {
	const notif = borogove.Push.receive(raw, persistence) as BorogovePushLike;
	return {
		accountId: typeof notif.accountId === "string" ? notif.accountId : "",
		chatId: typeof notif.chatId === "string" ? notif.chatId : "",
		type: pushType(notif.type),
		title: typeof notif.title === "string" ? notif.title : "",
		body: nonEmptyOrNull(notif.body),
		lang: nonEmptyOrNull(notif.lang),
		imageUri: nonEmptyOrNull(notif.imageUri),
		timestamp: nonEmptyOrNull(notif.timestamp),
		callStatus: nonEmptyOrNull(notif.callStatus),
	};
}

// ---------------------------------------------------------------------------
// searchMessages — try SDK-level search, fall back to client-side iteration
// ---------------------------------------------------------------------------

interface ClientWithSearch {
	searchMessages?: (q: string) => Promise<unknown[]> | unknown[];
	search?: (q: string) => Promise<unknown[]> | unknown[];
	getChats?: () => ReadonlyArray<unknown>;
}

interface ChatWithMessages {
	chatId?: unknown;
	getDisplayName?: () => string | null;
	getMessagesBefore?: (before: unknown, end?: unknown) => Promise<unknown[]>;
}

/**
 * Search messages on a raw client. Tries the SDK's `searchMessages` (or
 * `search`) method first; falls back to iterating each chat's recent history
 * and matching against a substring (case-insensitive).
 *
 * Both paths return typed `MessageMatch[]` with adapter-shaped `Message`
 * objects. Callers should not see raw borogove messages.
 *
 * @param abortSignal optional abort token — if provided, the search stops
 * paginating when `signal.aborted` becomes true. The promise still resolves
 * with the results gathered so far rather than rejecting.
 */
export async function searchMessages(
	rawClient: RawClient,
	query: string,
	abortSignal?: AbortSignal,
): Promise<MessageMatch[]> {
	if (query.length === 0) return [];
	const c = rawClient as unknown as ClientWithSearch;

	// SDK path
	const sdkFn = c.searchMessages ?? c.search;
	if (typeof sdkFn === "function") {
		const raw = await sdkFn.call(c, query);
		if (Array.isArray(raw)) {
			const out: MessageMatch[] = [];
			for (const item of raw) {
				try {
					const m = adaptMessage(item);
					out.push({ chatId: m.chatId, chatName: m.chatId, message: m });
				} catch {
					// skip unadaptable
				}
			}
			return out;
		}
	}

	// Fallback: iterate chats
	const chats = c.getChats?.() ?? [];
	const needle = query.toLowerCase();
	const out: MessageMatch[] = [];
	for (const rawChat of chats) {
		if (abortSignal?.aborted === true) return out;
		const chat = rawChat as ChatWithMessages;
		const chatId = typeof chat.chatId === "string" ? chat.chatId : "";
		if (chatId.length === 0) continue;
		const chatName = (typeof chat.getDisplayName === "function"
			? chat.getDisplayName() ?? null
			: null) ?? chatId;
		try {
			const messages = typeof chat.getMessagesBefore === "function"
				? await chat.getMessagesBefore(null, null)
				: [];
			for (const rawMsg of messages) {
				try {
					const m = adaptMessage(rawMsg);
					if (m.text.length > 0 && m.text.toLowerCase().includes(needle)) {
						out.push({ chatId, chatName, message: m });
					}
				} catch {
					// skip unadaptable message
				}
			}
		} catch {
			// skip chat that fails to load
		}
	}
	return out;
}
